import { Link } from "react-router-dom";
import Seo, { breadcrumbs, faqSchema, webPageSchema, serviceSchema } from "../components/Seo";
import LeadForm from "../components/LeadForm";
import CtaCallout from "../components/CtaCallout";
import { SectionHeader, Disclaimer } from "../components/UI";
import AboutScottBlurb from "../components/AboutScottBlurb";
import { siteConfig, disclaimers } from "../config";

const PAYMENT_FACTORS = [
  "Vehicle price","Down payment","Trade value","Payoff","Taxes","Fees","Interest rate","Loan term","Credit approval","Rebates or incentives",
];

const FAQS = [
  { q: "Can I plan around a payment?", a: "Yes — share your target payment range and Scott will help find vehicles that may fit, factoring in trade, down payment, and term." },
  { q: "Is a payment quote a financing offer?", a: "No. Payment numbers are estimates only and depend on credit approval and lender terms." },
  { q: "Does down payment matter as much as people say?", a: "Yes. More down lowers the financed amount and the payment, and can help with approval. Even a modest amount makes a meaningful difference over a 60–72 month term." },
  { q: "Will Scott share exact rates or approvals up front?", a: "No. Final rate, term, and approval come from the lender. Scott can give you a realistic direction and help line things up so the lender step is straightforward." },
];

export default function PaymentHelp() {
  return (
    <main id="main-content">
      <Seo
        title="Car Payment Help in Central Ohio | Scott Sells Chevys"
        description="Get a realistic direction on a new or used vehicle payment that may fit your budget. Scott walks through trade equity, down payment, term, and credit basics — no pressure."
        canonicalPath="/payment-help"
        jsonLd={[
          breadcrumbs([{ name: "Home", path: "/" }, { name: "Payment Help", path: "/payment-help" }]),
          faqSchema(FAQS),
          webPageSchema({ name: "Car Payment Help", path: "/payment-help", description: "Personal car payment help with Scott Hall in Central Ohio." }),
          serviceSchema({
            name: "Car Payment Direction",
            serviceType: "Auto finance pre-shopping help",
            path: "/payment-help",
            description: "Scott helps you sort out what affects a payment so you can shop a price range that actually fits your budget.",
          }),
        ]}
      />
      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-16">
          <h1 className="ssc-display text-3xl md:text-5xl font-extrabold">Trying to Find a Vehicle That Fits Your Budget?</h1>
          <p className="mt-3 text-[var(--ssc-silver)] max-w-2xl">Tell me your goal and I'll help you plan around it — no pressure.</p>
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <AboutScottBlurb
              heading="Payment Questions Should Not Feel Confusing"
              text="With over 15 years helping customers through vehicle purchases, Scott can help explain what affects a payment, including vehicle price, money down, trade value, payoff, taxes, fees, term, and credit approval."
              testId="payment-about-scott"
              className="mb-6"
            />
            <SectionHeader title="Tell me your payment goal" />
            <LeadForm
              leadType="payment_help"
              leadSource="Payment Help Page"
              testIdPrefix="pay-lead"
              fields={[
                { name: "name", label: "Name", required: true, autoComplete: "name" },
                { name: "phone", label: "Phone", inputMode: "tel", autoComplete: "tel" },
                { name: "email", label: "Email", type: "email", autoComplete: "email" },
                { name: "preferred_contact", label: "Preferred Contact", type: "select", options: ["Text", "Call", "Email"] },
                { name: "payment_range", label: "Desired Payment Range" },
                { name: "new_or_used", label: "New or Used", type: "select", options: ["New", "Used", "Either"] },
                { name: "vehicle_type", label: "Vehicle Type", type: "select", options: ["Truck", "SUV", "Car", "EV", "Not Sure"] },
                { name: "money_down", label: "Money Down" },
                { name: "trade_in", label: "Trade-In?", type: "select", options: ["Yes", "No"] },
                { name: "current_vehicle", label: "Current Vehicle" },
                { name: "estimated_payoff", label: "Estimated Payoff" },
                { name: "credit_situation", label: "Credit Situation (optional)", type: "select", options: ["Excellent", "Good", "Fair", "Rebuilding", "Prefer not to say"] },
                { name: "timeline", label: "Timeline", type: "select", options: ["Within a week", "1–4 weeks", "1–3 months", "Just planning"] },
              ]}
              submitLabel="Send to Scott"
            />
            <div className="mt-4"><Disclaimer text={disclaimers.payment} /></div>
            <CtaCallout
              testId="pay-cta-callout"
              heading="Want Scott to ballpark a payment direction?"
              sub="Send the basics — vehicle interest, trade, money down, target payment — and Scott will reply with a realistic range."
              actions={[
                { key: "text", label: "Text Scott", icon: "text", href: siteConfig.scott.phoneSms },
                { key: "trade", label: "Value My Trade", icon: "trade", to: "/value-my-trade" },
                { key: "find", label: "Find a Vehicle", icon: "find", to: "/find-me-a-vehicle" },
                { key: "ask", label: "Ask Scott", icon: "ask", to: "/contact" },
              ]}
            />
          </div>
          <aside className="space-y-5">
            <div className="ssc-card">
              <h3 className="font-extrabold text-[var(--ssc-navy)]">What affects your payment</h3>
              <ul className="mt-3 text-sm text-[var(--ssc-ink-2)] space-y-1 list-disc pl-5">
                {PAYMENT_FACTORS.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </div>
            <div className="ssc-card-dark">
              <div className="font-extrabold text-xl">Ready to apply?</div>
              <p className="text-[var(--ssc-silver)] text-sm mt-1">Apply through the official Byers Chevrolet finance link.</p>
              <a href={siteConfig.links.financeApply} target="_blank" rel="noopener noreferrer" className="ssc-btn ssc-btn-primary w-full mt-3" data-testid="pay-apply">Apply for Financing</a>
              <Link to="/scotts-deal-desk" className="ssc-btn ssc-btn-ghost w-full mt-2">Build My Own Deal</Link>
              <Link to="/contact" className="ssc-btn ssc-btn-ghost w-full mt-2">Contact Scott</Link>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
